import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { PermissionButton } from "@/components/ui/permission-button";
import { SectionTitle } from "@/components/ui/SectionTitle";
import { Spinner } from "@/components/ui/v2/spinner";
import { EventsCalendar } from "@/pages/(home)/(my-space)/events/event-details/_components/events-calendar";
import {
  COMPANY_EVENT_PATHS,
  DYNAMIC_EVENT_ID_KEY,
} from "@/routes/company/routes.events";
import {
  useCompanyCancelEventMutation,
  useCompanyGetEventByIdQuery,
} from "@/services/(company)/api.events";
import { EVENT_STATUSES } from "@/types/(company)/events.interface";
import { cn } from "@/utils/cn";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { startCase } from "lodash";
import {
  Building2,
  CalendarArrowUp,
  CalendarRange,
  ChevronLeft,
  Coffee,
  Info,
  Languages,
  MapPin,
  Paperclip,
  TextSearch,
  Theater,
  User,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import { EventParticipants } from "./event-participants";
import { EventReportButton } from "./event-report-generator";

import { formatDateDmyComma, formatPerson, formatTime } from "@/utils/helper";

export const EventInfo = () => {
  const { t } = useTranslation(["common", "events"]);
  const navigate = useNavigate();
  const { eventId } = useParams<{ [DYNAMIC_EVENT_ID_KEY]: string }>();
  const { data, isFetching } = useCompanyGetEventByIdQuery(
    { eventId: eventId as string },
    { skip: !eventId },
  );
  const event = data?.data;

  const eventData: {
    label: string;
    value: string | ReactNode;
    icon: LucideIcon;
    iconClasses: string;
  }[] = [
    {
      label: t("events:shared.event_name"),
      value: event?.title,
      icon: CalendarRange,
      iconClasses: "bg-info-muted text-info",
    },
    {
      label: t("events:shared.event_status"),
      value: startCase(event?.status),
      icon: Theater,
      iconClasses: "bg-info-muted text-info",
    },
    {
      label: t("events:shared.event_type"),
      value: startCase(event?.type),
      icon: CalendarRange,
      iconClasses: "bg-orange-100 text-orange-500",
    },
    {
      label: t("events:shared.start_date"),
      value: event?.startDateTime
        ? formatDateDmyComma(event?.startDateTime)
        : null,
      icon: CalendarRange,
      iconClasses: "bg-green-100 text-green-500",
    },
    {
      label: t("events:shared.end_date"),
      value: event?.startDateTime
        ? formatDateDmyComma(event?.endDateTime)
        : null,
      icon: CalendarRange,
      iconClasses: "bg-green-100 text-green-500",
    },
    {
      label: t("events:shared.location"),
      value: event?.location,
      icon: MapPin,
      iconClasses: "bg-green-100 text-green-500",
    },
    {
      label: t("events:shared.description"),
      value: event?.description,
      icon: Info,
      iconClasses: "bg-amber-100 text-amber-500",
    },
    {
      label: t("events:shared.event_recurrence"),
      value: startCase(event?.recurrence),
      icon: CalendarArrowUp,
      iconClasses: "bg-info-muted text-info",
    },
    ...(event?.language
      ? [
          {
            label: t("events:shared.language"),
            value: event.language,
            icon: Languages,
            iconClasses: "bg-info-muted text-info",
          },
        ]
      : []),
    ...(event?.branch
      ? [
          {
            label: t("events:shared.branch"),
            value: event.branch.name,
            icon: Building2,
            iconClasses: "bg-indigo-100 text-indigo-500",
          },
        ]
      : []),
    {
      label: t("events:shared.attachments"),
      icon: Paperclip,
      value: (
        <ul className="grid grid-flow-row gap-0.5">
          {event?.attachments?.length
            ? event?.attachments.map((file) => (
                <li key={file.id}>
                  <Link
                    to={file.fileUrl}
                    target="_blank"
                    className="text-primary underline"
                  >
                    {file.fileName}
                  </Link>
                </li>
              ))
            : "No attachments"}
        </ul>
      ),
      iconClasses: "bg-red-100 text-red-500",
    },
    {
      label: t("events:shared.created_by"),
      value: formatPerson(event?.createdBy),
      icon: User,
      iconClasses: "bg-pink-100 text-pink-500",
    },
  ];

  const [cancelEvent, { isLoading: isCanceling }] =
    useCompanyCancelEventMutation();
  const handleCancelEvent = async (eventId: string) => {
    const id = toast.loading("Processing...");
    await cancelEvent({ eventId })
      .unwrap()
      .then((res) => toast.success(res?.message, { id }))
      .catch((e) => {
        toast.error(e?.data?.message ?? e?.message, { id });
        console.error(e);
      });
  };

  if (!isFetching && !data)
    return (
      <EmptyState
        icon={TextSearch}
        title={t("common:a11y.event_not_found")}
        description={t("events:shared.event_not_found_desc")}
      />
    );
  return (
    <Card className="min-h-60">
      {isFetching ? (
        <Spinner />
      ) : (
        <div className="flex flex-col lg:flex-row w-full">
          {/* Left: Event Details */}
          <div className="flex flex-col w-full lg:w-[70%]">
            <CardHeader className="flex flex-row items-center gap-2">
              <Button
                size="icon"
                variant="outline"
                aria-label={t("my-space:events.back_to_events")}
                data-tooltip-id="hrm-tooltip"
                data-tooltip-content={t("my-space:events.back_to_events")}
                className="w-max"
                onClick={() => navigate(COMPANY_EVENT_PATHS.ROOT)}
              >
                <ChevronLeft className="text-primary rtl:rotate-180" />
              </Button>
              <div className="text-sm">
                <SectionTitle
                  title={t("common:a11y.event_information")}
                  noMarginBottom
                />
                <p className="text-sm -mt-1 text-black/50 max-w-sm truncate">
                  {event?.title}
                </p>
              </div>
            </CardHeader>

            <CardContent>
              {/* Poster / banner */}
              {event?.posterUrl && (
                <img
                  src={event.posterUrl}
                  alt={t("common:a11y.event_poster_alt")}
                  className="mb-6 max-h-64 w-full rounded-sxs border object-cover"
                />
              )}

              <SectionTitle
                title={t("my-space:page_titles.event_details")}
                size="sm"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-8">
                {eventData.map(
                  ({ label, value, icon: Icon, iconClasses }, i) => (
                    <div key={i} className="h-max flex min-w-48 gap-4">
                      <div
                        className={cn(
                          "p-2 size-10 rounded-sxs bg-info-muted grid place-items-center",
                          iconClasses,
                        )}
                      >
                        <Icon />
                      </div>
                      <div className="text-sm">
                        <p className="uppercase text-black/50">{label}</p>
                        <span className="font-medium">{value}</span>
                      </div>
                    </div>
                  ),
                )}
              </div>

              {/* Breaks / agenda */}
              {event?.breaks && event.breaks.length > 0 && (
                <>
                  <hr className="border-border my-8" />
                  <SectionTitle
                    title={t("events:company.create_event.form.breaks")}
                    size="sm"
                  />
                  <ul className="mt-2 space-y-2">
                    {event.breaks.map((brk, i) => (
                      <li
                        key={brk.id ?? i}
                        className="flex items-center gap-3 rounded-sxs border p-2 text-sm"
                      >
                        <div className="grid size-9 place-items-center rounded-sxs bg-info-muted text-info">
                          <Coffee className="size-4" />
                        </div>
                        <div className="flex flex-1 items-center justify-between">
                          <span className="font-medium">
                            {brk.title ||
                              t("events:company.create_event.form.break_fallback")}
                          </span>
                          <span className="text-black/60">
                            {formatTime(brk.startDateTime)} –{" "}
                            {formatTime(brk.endDateTime)}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* Participants table */}
              <hr className="border-border my-8" />
              <EventParticipants eventId={eventId!} />
            </CardContent>

            <CardFooter className="flex justify-end flex-wrap gap-2 flex-1">
              <PermissionButton
                section={PLATFORM_SECTION_ENUM.EVENTS}
                action="edit"
                className="min-w-32"
                onClick={() => navigate(COMPANY_EVENT_PATHS.EDIT(eventId!))}
              >
                {t("common:buttons.edit")}
              </PermissionButton>
              <EventReportButton eventId={eventId!} />
              {event?.status !== EVENT_STATUSES.CANCELLED && (
                <PermissionButton
                  section={PLATFORM_SECTION_ENUM.EVENTS}
                  action="edit"
                  className="min-w-32"
                  variant="danger"
                  isLoading={isCanceling}
                  onClick={() => handleCancelEvent(eventId!)}
                >
                  {t("common:buttons.cancel")}
                </PermissionButton>
              )}
            </CardFooter>
          </div>

          {/* Right: Calendar */}
          <div className="w-full lg:w-[30%] lg:border-s border-t lg:border-t-0 p-4">
            <EventsCalendar />
          </div>
        </div>
      )}
    </Card>
  );
};
